import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _debounce from 'lodash/debounce'
import _size from 'lodash/size'
import _tail from 'lodash/tail'
import _map from 'lodash/map'
import _forEach from 'lodash/forEach'
import _toUpper from 'lodash/toUpper'

// Components
import SearchInput from './SearchInput'
import SearchIndicator from './SearchIndicator'

// Constants
import {
  LLD_SEARCH_URL,
} from '../constants'

import getUrl from 'hfty-utils/url'

// Styles
import { error } from 'hfty-styles/src/colors'

const errorStyle = {
  padding: 5,
  maxWidth: 350,
  backgroundColor: error(0.8),
  fontSize: 12,
  whiteSpace: 'normal',
  lineHeight: '1.5em',
}

// Local
const pattern = /^([A-Z]+)[- ]+([0-9]+)[- ]+([0-9]+)[- ]+([0-9]+)[- ]+([A-Z]+[0-9]+)$/i

// City Search
export default class LldSearch extends Component {
  constructor () {
    super()

    this.state = {
      term: '',
      searching: [],
      errors: [],
    }

    this.search = this.search.bind(this)
    this.onChangeInput = this.onChangeInput.bind(this)
    this.onSelectResult = this.onSelectResult.bind(this)
    this.onSearch = _debounce(this.onSearch.bind(this), 500)
  }

  componentDidMount () {
    this._isMounted = true
  }

  componentWillUnmount () {
    this._isMounted = false
  }

  search () {
    const { term } = this.state
    const match = term.match(pattern)

    if (match && _size(match) >= 6) {
      const [, quarter, section, township, range, meridian] = match
      const url = getUrl({
        path: `${LLD_SEARCH_URL}` +
          `?meridian=${meridian}&range=${range}&township=${township}&section=${section}&quarter=${quarter}`,
        token: this.context.token,
      })

      fetch(url).then((response) => {
        const { status } = response

        if (status >= 200 && status < 300) {
          response.json().then((results) => {
            if (this._isMounted) {
              this.setState({
                searching: _tail(this.state.searching),
              })

              let minLng = 1000
              let minLat = 1000
              let maxLng = -1000
              let maxLat = -1000

              try {
                const spatials = JSON.parse(results.detail)
                if (spatials.length !== 0) {
                  _forEach(spatials, function (item) {
                    const coords = item.coordinates[0][0]
                    _forEach(coords, function (coord) {
                      minLat = Math.min(minLat, coord[1])
                      maxLat = Math.max(maxLat, coord[1])
                      minLng = Math.min(minLng, coord[0])
                      maxLng = Math.max(maxLng, coord[0])
                    })
                  })
                }
              } catch (error) {
              } finally {
                if (minLng && minLat && maxLng && maxLat) {
                  this.props.onZoomToBounds([
                    [minLat, minLng],
                    [maxLat, maxLng],
                  ])
                }
              }
            }
          })
        }
      })
    }
  }

  isSearching () {
    return _size(this.state.searching) > 0
  }

  onChangeInput ({target}) {
    this.setState({
      term: _toUpper(target.value),
    }, this.onSearch)
  }

  onSearch () {
    const valid = this.validateTerm()
    const { term } = this.state
    const match = term.match(pattern)

    if (valid && match) {
      this.setState({
        searching: [...this.state.searching, true],
        errors: [],
      }, this.search)
    }
  }

  validateTerm () {
    const { term } = this.state
    const match = term && term.match(pattern)

    if (match) {
      const [, quarter, section, township, range, meridian] = match

      if (!quarter || !quarter.match(/^NW|^NE$|^SW$|^SE$/)) {
        this.setState({
          errors: ['Quarter section must be NW, NE, SW or SE.'],
        })

        return false
      }

      const sectionNr = parseInt(section)
      if (!sectionNr || sectionNr < 1 || sectionNr > 36) {
        this.setState({
          errors: ['Section must be a number between 1 and 36.'],
        })

        return false
      }

      const townshipNr = parseInt(`${township}`.replace(/[^0-9]/, ''))
      if (!townshipNr || townshipNr < 1 || townshipNr > 126) {
        this.setState({
          errors: ['Township must be a number between 1 and 126.'],
        })

        return false
      }

      const meridianNr = parseInt(meridian.charAt(1), 10)
      const rangeLimit = meridianNr === 1 ? 33 : 30
      const rangeNr = parseInt(range.replace(/[^0-9]/), '')
      const letterPos = range.search(/[a-zA-Z]/)

      if (letterPos !== -1 || rangeNr < 1 || rangeNr > rangeLimit) {
        this.setState({
          errors: [`Range must be a number between 1 and ${rangeLimit}.`],
        })

        return false
      }

      if (!`${meridian}`.match(/^[WE]{1}[1-7]{1}$/)) {
        this.setState({
          errors: ['Meridian is required and must begin with W or E, followed by a number from 1 to 7.'],
        })

        return false
      }
    }

    return true
  }

  onSelectResult (result) {
    const {
      boundingbox,
      lat,
      lon: lng,
    } = result

    if (boundingbox) {
      const [minY, maxY, minX, maxX] = boundingbox
      this.props.onZoomToBounds([
        [minY, minX],
        [maxY, maxX],
      ])
    } else {
      this.props.onZoomToLatLng({lat, lng})
    }
  }

  render () {
    return (
      <div data-test='LldSearch'>
        <div style={{
          display: 'flex',
        }}>
          <SearchInput
            value={this.state.term}
            onInput={this.onChangeInput}
            placeholder={'e.g. SW 9-1-15 W3'}
            pattern={pattern.source}
          />
        </div>
        {this.isSearching() ? (
          <SearchIndicator />
        ) : null}
        {_size(this.state.errors) ? (
          <div style={errorStyle}>
            {_map(this.state.errors, (error) => (
              <div>
                {error}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    )
  }
}

LldSearch.propTypes = {
  // latlng: PropTypes.bool,
  onZoomToLatLng: PropTypes.func.isRequired,
  onZoomToBounds: PropTypes.func.isRequired,
}

LldSearch.contextTypes = {
  token: PropTypes.string.isRequired,
}
